/**
 * DI-3-F — the runner's upload half (story §2.1 / §2.2; N2, N8).
 *
 * The runner's own queue does one request at a time (creates, round trips,
 * `metadata-complete`); the upload runs BESIDE it, one loop per set, all
 * sharing the tab-wide `UploadQueue` (4 PUTs in flight for the whole tab,
 * sets served in Start order). When a set's loop may start is N2:
 *
 *   "now"    once the set's round trip is `synced` — never before (the
 *            uplink must not delay the metadata of any set; `reached(phase,
 *            "synced")`);
 *   "after"  nothing until the pilot's Approve answered
 *            `approved_pending_upload` (`upload/approvals.ts` →
 *            `approved()` → `upload.approvedAt`); then the same loop — the
 *            ingest promotes the set to PROCESSING by itself when the last
 *            blob verifies (DI-3-B `approved_at` + DI-3-Bb): no second click;
 *   partner  (`uploadChoice === null`: the workflow withholds) no upload
 *            ever; the set's status is read while it is in the intake and
 *            `POST …/withhold` is sent whenever it reads `verdict_ready` /
 *            `needs_info` after another state (DI-3-P10: a serial edit on a
 *            withheld set re-matches it; it is withheld again) →
 *            `withheld_complete` (Approve then finalizes metadata-only).
 *   escape   M5-NODE-01 — "Upload images…" on a withheld set under an
 *            `optional` version: an upload like "now" whose every session
 *            open carries the set's `uploadReason` (inspection-3/v1 2.6.0);
 *            a refusal (409 `upload_blocked`, 400 `reason_required`) ends
 *            the upload `failed` with its words, never a retry loop.
 *
 * A pass is `upload/uploader.ts`; after it: COMPLETED → `present` (with the
 * poll's `dataAcquisitionState` — `PROCESSING` = promoted); FAILED with files
 * to re-send (`blob_absent` / `blob_mismatch`) → they leave the ledger and ONE
 * automatic retry pass sends them in a new session; failed again → `failed`
 * with the count, and "Retry the upload" (`retry()`) starts a pass by hand. A
 * transport stop keeps the phase and tries again after `retryMs` (edge ak).
 *
 * DI-3-S9 (smoke F8) — a RESUME asks the server first: the first time this
 * driver is about to start a pass of a set whose ledger is NOT empty (a new
 * tab, a reload, the upload lane — a driver that did not write that ledger),
 * it reads which originals the server KNOWS it lacks (`api.missingBlobs` =
 * `listIntakeBlobs?state=missing`, every page), drops exactly those ledger
 * entries (`ledger.ts:reconcile` — never adds one; an entry the server has not
 * verified stays, DI-3-S4-FE's skip), persists the ledger, and only then lets
 * the pass compute what to send (`pendingFiles`). A fresh upload (an empty
 * ledger at that point) never reads it; a pass that resumes at its manifest's
 * poll (`committing` / `ingesting`) needs no ledger and is not reconciled. The
 * read failing → the pass proceeds on the ledger as before S9, and says so.
 * A store that lost blobs BEFORE any ingest checked them is not known to the
 * server: the ingest's `evict` after a FAILED run stays the safety net,
 * unchanged.
 */
import type { StoreApi } from "zustand";
import type { IndexState } from "../indexStore.js";
import { sendPlan } from "./sets.js";
import { wireNames } from "../sync/toDataPointInput.js";
import { fileKey } from "../add/diff.js";
import { uploadStartable } from "./uploadPending.js";
import { patchSet, type RunnerSet, type RunnerState } from "./runnerStore.js";
import { updateCluster } from "../session/sessionStore.js";
import { isIntakeFetchError, reasonOf } from "../api/intakeFetch.js";
import type { UploadApi } from "../upload/api.js";
import type { IntakeStatus } from "../api/intakeTypes.js";
import type { FetchLike } from "../upload/blockUpload.js";
import { evict, pendingFiles, reconcile } from "../upload/ledger.js";
import {
  RESEND_CODES,
  passManifest,
  refusedFiles,
  type UploadFile,
} from "../upload/manifest.js";
import { UploadQueue } from "../upload/queue.js";
import {
  MAX_AUTO_PASSES,
  uploadNeedsFiles,
  uploadNowOffered,
  type SetUpload,
  type SetWithhold,
} from "../upload/state.js";
import {
  FINAL_REFUSALS,
  UploadRefusedError,
  UploadTransportError,
  runUploadPass,
} from "../upload/uploader.js";

/** Edge ak: "upload stopped at 61 % — retrying every 5 min". */
export const UPLOAD_RETRY_MS = 5 * 60 * 1000;
/** A partner set's status read while it has not reached its verdict. */
export const WITHHOLD_POLL_MS = 5_000;
/** … and while it waits for a flight log (as the board: 30 s). */
export const WITHHOLD_AWAITING_POLL_MS = 30_000;

const WITHHOLD_AT: ReadonlySet<string> = new Set([
  "verdict_ready",
  "needs_info",
]);

/**
 * DI-3-P10 — what a verdict IS, for the withhold's "due again" rule: the
 * state, the light and blockers, each flight's serials / enabled flag and the
 * stages' finish times. A re-match after a serial edit always changes it (the
 * serials it wrote at least); a read that says the same verdict as the one
 * just withheld does not.
 */
export function verdictKey(st: IntakeStatus): string {
  return JSON.stringify([
    st.intakeState,
    st.light ?? null,
    (st.blockers ?? []).map((b) => [b.key, b.flightId ?? null]),
    ((st.flights as IntakeStatus["flights"] | undefined) ?? []).map((f) => [
      f.flightId,
      f.enabled,
      f.serialsSource ?? null,
      f.serials ?? null,
    ]),
    (st.stages ?? []).map((x) => [x.key, x.state, x.finishedAt]),
  ]);
}

export interface DriverHost {
  state: StoreApi<RunnerState>;
  index: StoreApi<IndexState>;
  fileOf: (index: number) => Blob | null;
  persist: <T>(fn: () => Promise<T>) => Promise<T>;
  now: () => Date;
}

export interface DriverDeps {
  api: UploadApi;
  fetch?: FetchLike | undefined;
  sleep?: ((ms: number) => Promise<void>) | undefined;
  pollMs?: number | undefined;
  retryMs?: number | undefined;
  withholdPollMs?: number | undefined;
  queue?: UploadQueue | undefined;
}

/**
 * The files a set uploads: the rows its round trip sent, under their wire
 * names. DI-3-G — an add keeps only its NEW files (`keys`, the diff's
 * `fileKey`s); the wire names are still those of the whole round trip, so a
 * `~n` twin keeps the name its data point carries.
 */
export function uploadFilesOf(
  rows: IndexState["rows"],
  memberIndexes: readonly number[],
  keys?: readonly string[],
): UploadFile[] | null {
  if (memberIndexes.length === 0) return null;
  const plan = sendPlan(rows, memberIndexes, true);
  const names = wireNames(plan.send);
  const keep = keys ? new Set(keys) : null;
  const send = keep
    ? plan.send.filter((r) => keep.has(fileKey(r.hashes)))
    : plan.send;
  return send.map((r) => ({
    filename: names.get(r.index) ?? r.name,
    sizeBytes: r.size,
    md5Hex: r.hashes.md5Hex,
    capturedAtMs: r.parsed.normalized.capturedAtMs ?? null,
    index: r.index,
  }));
}

/**
 * DI-3-S68 B3 — "rows before bytes": does the runner send rows or a
 * `metadata-complete` now (a set in `syncing` / `completing`), or work its
 * queue at all (`busy` — between two such steps)? Then no new PUT starts.
 */
export function rowsBeforeBytes(
  s: Pick<RunnerState, "sets" | "busy">,
): boolean {
  return (
    s.busy ||
    s.sets.some((x) => x.phase === "syncing" || x.phase === "completing")
  );
}

let driverSeq = 0;

export class UploadDriver {
  readonly queue: UploadQueue;
  /** DI-3-S68 B3 — this driver's key in the queue's holders. */
  private readonly holdKey = `driver-${String(++driverSeq)}`;
  private readonly offHold: () => void;
  private readonly loops = new Set<string>();
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  /** DI-3-S9 — sets whose first pass in THIS driver was reached (reconciled or fresh). */
  private readonly firstPassSeen = new Set<string>();
  private disposed = false;

  constructor(
    private readonly host: DriverHost,
    private readonly deps: DriverDeps,
  ) {
    this.queue = deps.queue ?? new UploadQueue();
    // DI-3-S68 B3 — the runner's rows go before the bytes
    this.offHold = host.state.subscribe((s) => {
      this.queue.hold(this.holdKey, rowsBeforeBytes(s));
    });
    this.queue.hold(this.holdKey, rowsBeforeBytes(host.state.getState()));
  }

  /** Start every loop that may run now. Cheap: called on every transition. */
  pump(): void {
    if (this.disposed) return;
    for (const s of this.host.state.getState().sets) {
      if (uploadStartable(s)) void this.drive(s.clusterId);
      else if (this.withholds(s)) void this.driveWithhold(s.clusterId);
    }
  }

  /** Approve answered `approved_pending_upload`; true when a set of THIS runner is it. */
  approved(daId: string, at: string): boolean {
    const s = this.host.state
      .getState()
      .sets.find((x) => x.created?.dataAcquisitionId === daId);
    if (!s?.upload) return false;
    if (!s.upload.approvedAt)
      void this.apply(s.clusterId, { approvedAt: at }, true);
    this.pump();
    return true;
  }

  /**
   * DI-3-P7 — "Upload now" on an "after verification" set whose upload has
   * not started (N2: the pilot's own act, never forced): the choice flips to
   * "now" in the runner AND in the session record (a resume keeps it), then
   * the same loop as a "now" set starts — once its round trip is `synced`
   * (`uploadStartable`). No second uploader. Refused (false) once a byte is
   * up, on a withholding workflow, or for a set this runner does not hold.
   */
  uploadNow(clusterId: string): boolean {
    const s = this.set(clusterId);
    if (!s?.upload || !uploadNowOffered(s)) return false;
    const upload: SetUpload = {
      ...s.upload,
      nowAt: this.host.now().toISOString(),
    };
    patchSet(this.host.state, clusterId, { uploadChoice: "now", upload });
    const sid = this.host.state.getState().sessionId;
    const persisted = sid
      ? this.host.persist(() => {
          const latest = this.set(clusterId)?.upload ?? upload;
          return updateCluster(sid, clusterId, {
            uploadChoice: "now",
            upload: latest,
          });
        })
      : Promise.resolve(null);
    void persisted.then(() => {
      this.pump();
    });
    return true;
  }

  /** "Retry the upload": a new pass over what the last one could not get verified. */
  retry(clusterId: string): void {
    const s = this.set(clusterId);
    const u = s?.upload;
    if (!s || !u) return;
    // DI-3-S68 T1 — refused for good by the server: nothing to retry
    if (u.ended) return;
    if (u.phase === "failed")
      void this.apply(
        clusterId,
        {
          phase: "queued",
          pass: u.pass + 1,
          sessionId: null,
          jobId: null,
          error: null,
          retryAt: null,
          finishedAt: null,
        },
        true,
      ).then(() => {
        this.pump();
      });
    else if (u.error) {
      void this.apply(clusterId, { error: null, retryAt: null }, false).then(
        () => {
          this.pump();
        },
      );
    }
  }

  pause(): void {
    this.queue.pause();
    this.host.state.setState({ uploadsPaused: true });
  }

  resume(): void {
    this.queue.resume();
    this.host.state.setState({ uploadsPaused: false });
  }

  dispose(): void {
    this.disposed = true;
    this.offHold();
    this.queue.hold(this.holdKey, false);
    for (const t of this.timers.values()) clearTimeout(t);
    this.timers.clear();
  }

  // ── internals ─────────────────────────────────────────────────────────

  private set(clusterId: string): RunnerSet | undefined {
    return this.host.state
      .getState()
      .sets.find((x) => x.clusterId === clusterId);
  }

  private sleep(ms: number): Promise<void> {
    if (this.deps.sleep) return this.deps.sleep(ms);
    return new Promise((r) => {
      setTimeout(r, ms);
    });
  }

  private later(key: string, ms: number): void {
    if (this.disposed || this.timers.has(key)) return;
    this.timers.set(
      key,
      setTimeout(() => {
        this.timers.delete(key);
        this.pump();
      }, ms),
    );
  }

  /** Merge `patch` into the set's upload; `persist` writes the whole upload. */
  private async apply(
    clusterId: string,
    patch: Partial<SetUpload>,
    persist: boolean,
  ): Promise<void> {
    const cur = this.set(clusterId)?.upload;
    if (!cur) return;
    patchSet(this.host.state, clusterId, { upload: { ...cur, ...patch } });
    const sid = this.host.state.getState().sessionId;
    if (!persist || !sid) return;
    await this.host.persist(() => {
      const latest = this.set(clusterId)?.upload;
      return latest
        ? updateCluster(sid, clusterId, { upload: latest })
        : Promise.resolve(null);
    });
  }

  private async drive(clusterId: string): Promise<void> {
    if (this.loops.has(clusterId)) return;
    this.loops.add(clusterId);
    try {
      for (;;) {
        if (this.disposed) return;
        const s = this.set(clusterId);
        const daId = s?.created?.dataAcquisitionId;
        if (!s || !daId || !uploadStartable(s) || !s.upload) return;
        const u = s.upload;
        const nowMs = this.host.now().getTime();
        if (u.retryAt && Date.parse(u.retryAt) > nowMs) {
          this.later(`u:${clusterId}`, Date.parse(u.retryAt) - nowMs);
          return;
        }
        const all = uploadFilesOf(
          this.host.index.getState().rows,
          s.memberIndexes,
          s.uploadKeys,
        );
        if (uploadNeedsFiles(u) && !all) return; // waits for the folder (resume)
        const files = passManifest(all ?? [], new Set(u.verified));
        // DI-3-S9 — the reconcile point: the first pass of a RESUME
        const key = `${this.host.state.getState().sessionId ?? ""}:${clusterId}`;
        if (!this.firstPassSeen.has(key)) {
          this.firstPassSeen.add(key);
          if (
            all &&
            Object.keys(u.ledger).length > 0 &&
            u.phase !== "committing" &&
            u.phase !== "ingesting"
          ) {
            await this.reconcileWithServer(clusterId, daId, files);
            continue; // read the set again: the ledger changed
          }
        }
        if (all && files.length === 0) {
          // every file verified already
          await this.apply(
            clusterId,
            { phase: "present", error: null, retryAt: null },
            true,
          );
          return;
        }
        let result;
        try {
          result = await runUploadPass({
            daId,
            files,
            upload: u,
            setOrder: s.ordinal,
            fileOf: this.host.fileOf,
            api: this.deps.api,
            queue: this.queue,
            now: this.host.now,
            sleep: (ms) => this.sleep(ms),
            fetch: this.deps.fetch,
            pollMs: this.deps.pollMs,
            update: (patch, persist) => this.apply(clusterId, patch, persist),
            // M5-NODE-01 — the escape hatch's reason rides every open
            reason: s.uploadReason ?? null,
          });
        } catch (e) {
          if (e instanceof UploadRefusedError) {
            // M5-NODE-01 — 409 `upload_blocked` / 400 `reason_required`: no
            // retry fixes a refusal; the upload stops with the words.
            // DI-3-S68 T1 — 409 `da_state_not_uploadable` / 404 on the open:
            // ended for good (`ended`), never started again
            await this.apply(
              clusterId,
              {
                phase: "failed",
                failedCount: 0,
                error: e.message,
                retryAt: null,
                finishedAt: this.host.now().toISOString(),
                ...(FINAL_REFUSALS.has(e.reason)
                  ? {
                      ended: e.reason as "da_state_not_uploadable" | "set_gone",
                    }
                  : {}),
              },
              true,
            );
            return;
          }
          const message =
            e instanceof UploadTransportError
              ? e.message
              : "The upload stopped; retrying.";
          const retryMs = this.deps.retryMs ?? UPLOAD_RETRY_MS;
          await this.apply(
            clusterId,
            {
              error: message,
              retryAt: new Date(
                this.host.now().getTime() + retryMs,
              ).toISOString(),
            },
            true,
          );
          this.later(`u:${clusterId}`, retryMs);
          return;
        }
        const at = this.host.now().toISOString();
        const cur = this.set(clusterId)?.upload ?? u;
        const again = cur.pass < MAX_AUTO_PASSES;
        if (result.kind === "put_failed") {
          // the blocks did not go up (after their retries): no manifest was
          // completed; the next pass sends these files again
          await this.apply(
            clusterId,
            again
              ? {
                  phase: "queued",
                  pass: cur.pass + 1,
                  refused: result.files,
                  missing: null,
                  sessionId: null,
                  jobId: null,
                }
              : {
                  phase: "failed",
                  refused: result.files,
                  missing: null,
                  failedCount: result.files.length,
                  finishedAt: at,
                },
            true,
          );
          if (again) continue;
          return;
        }
        const status = result.outcome.status;
        if (result.kind === "completed") {
          await this.apply(
            clusterId,
            {
              phase: "present",
              processed: status.processed,
              total: status.total,
              dataAcquisitionState: status.dataAcquisitionState ?? null,
              verified: [
                ...new Set([...cur.verified, ...files.map((f) => f.filename)]),
              ],
              refused: [],
              missing: null,
              failedCount: 0,
              error: null,
              retryAt: null,
              finishedAt: at,
            },
            true,
          );
          return;
        }
        // FAILED: what verified is verified; the refused files leave the ledger
        const { named, unnamed } = refusedFiles(status.errors);
        const refusedSet = new Set(named);
        const verified = [
          ...new Set([
            ...cur.verified,
            ...(unnamed === 0
              ? files.map((f) => f.filename).filter((n) => !refusedSet.has(n))
              : []),
          ]),
        ];
        const resend = status.errors.filter(
          (e) => e.filename && e.code && RESEND_CODES.has(e.code),
        ).length;
        // DI-3-S9 — the originals the server lacks: the card names them
        const missing = new Set(
          status.errors
            .filter((e) => e.filename && e.code === "blob_absent")
            .map((e) => e.filename),
        ).size;
        const base = {
          refused: named,
          missing: missing > 0 ? missing : null,
          verified,
          ledger: evict(cur.ledger, named),
          sessionId: null,
          jobId: null,
          dataAcquisitionState: status.dataAcquisitionState ?? null,
        };
        if (again && unnamed === 0 && resend > 0 && resend === named.length) {
          // ONE automatic retry pass: those files again, in a new session
          await this.apply(
            clusterId,
            { ...base, phase: "queued", pass: cur.pass + 1 },
            true,
          );
          continue;
        }
        await this.apply(
          clusterId,
          {
            ...base,
            phase: "failed",
            failedCount: Math.max(1, named.length + unnamed),
            finishedAt: at,
          },
          true,
        );
        return;
      }
    } finally {
      this.loops.delete(clusterId);
    }
  }

  /**
   * DI-3-S9 — the resume's reconcile: "Checking with the server what is
   * already there…" (not persisted), the names the server knows missing, the
   * ledger minus those (persisted), and what the pass will send. A failed
   * read leaves the ledger as it is ("could not check the server — sending
   * what the ledger lacks").
   */
  private async reconcileWithServer(
    clusterId: string,
    daId: string,
    files: readonly UploadFile[],
  ): Promise<void> {
    const pass = this.set(clusterId)?.upload?.pass ?? 1;
    await this.apply(
      clusterId,
      { reconcile: { state: "checking", pass } },
      false,
    );
    let missing: ReadonlySet<string>;
    try {
      missing = await this.deps.api.missingBlobs(daId);
    } catch {
      await this.apply(
        clusterId,
        { reconcile: { state: "failed", pass } },
        true,
      );
      return;
    }
    const cur = this.set(clusterId)?.upload;
    if (!cur) return;
    const ledger = reconcile(cur.ledger, missing);
    await this.apply(
      clusterId,
      {
        ledger,
        reconcile: {
          state: "done",
          pass,
          marked: Object.keys(cur.ledger).length,
          kept: Object.keys(ledger).length,
          knownMissing: missing.size,
          sending: pendingFiles(ledger, files).length,
        },
      },
      true,
    );
  }

  // ── the partner set: withhold whenever due (DI-3-P10) ───────────────

  /**
   * A partner set's loop runs while the set is in the intake — not only
   * until the first withhold: a serial edit on a withheld set moves it
   * `withheld_complete → needs_info → matching → verdict_ready` (intake/v1
   * 1.13.1), and the runner withholds it again. A refusal other than
   * `intake_state_invalid` ends it for good ("refused → never again").
   */
  private withholds(s: RunnerSet): boolean {
    if (s.uploadChoice !== null || !s.created || s.phase !== "running")
      return false;
    return s.withhold?.state !== "refused";
  }

  private async applyWithhold(
    clusterId: string,
    w: SetWithhold,
  ): Promise<void> {
    patchSet(this.host.state, clusterId, { withhold: w });
    const sid = this.host.state.getState().sessionId;
    if (sid)
      await this.host.persist(() =>
        updateCluster(sid, clusterId, { withhold: w }),
      );
  }

  /**
   * DI-3-F + DI-3-P10 — the partner set's status is read on the poll; the
   * withhold is sent when the status reads `verdict_ready` / `needs_info`
   * AND it is due: a successful withhold remembers the verdict it withheld
   * (`verdictKey`); it is due again once a read shows another state
   * (`withheld_complete`, `matching`, …) or a different verdict — the other
   * readers of the status (the card's own poll) may consume the short
   * `matching` window, so a changed verdict counts as the change. A re-match
   * after a serial edit is withheld again exactly once, never on every poll
   * tick. The server answers 200 either way; after a re-match it
   * re-stamps every non-present row and counts them all (527, not 0 —
   * measured on the BE, P10 §10a), so the driver reads the STATE, never the
   * count. The loop ends when the set left the intake (`intakeState` null),
   * on a refusal, or when the runner is disposed.
   */
  private async driveWithhold(clusterId: string): Promise<void> {
    const key = `w:${clusterId}`;
    if (this.loops.has(key)) return;
    this.loops.add(key);
    /** The verdict last withheld; null = due at the next due state. */
    let withheldAt: string | null = null;
    try {
      for (;;) {
        if (this.disposed) return;
        const s = this.set(clusterId);
        const daId = s?.created?.dataAcquisitionId;
        if (!s || !daId || !this.withholds(s)) return;
        let wait = this.deps.withholdPollMs ?? WITHHOLD_POLL_MS;
        try {
          const st = await this.deps.api.status(daId);
          const at = this.host.now().toISOString();
          if (st.intakeState === null) return; // finalized — nothing to withhold
          if (st.intakeState === "withheld_complete") {
            withheldAt = null;
            if (s.withhold?.state !== "done")
              await this.applyWithhold(clusterId, {
                state: "done",
                at,
                imagesWithheld: s.withhold?.imagesWithheld ?? null,
                reason: null,
              });
          } else if (WITHHOLD_AT.has(st.intakeState)) {
            const verdict = verdictKey(st);
            if (withheldAt !== verdict) {
              try {
                const r = await this.deps.api.withhold(daId);
                withheldAt = verdict;
                await this.applyWithhold(clusterId, {
                  state: "done",
                  at: this.host.now().toISOString(),
                  imagesWithheld: r.imagesWithheld,
                  reason: null,
                });
              } catch (e) {
                const reason = reasonOf(e);
                if (
                  isIntakeFetchError(e) &&
                  e.status === 409 &&
                  reason !== "intake_state_invalid"
                ) {
                  await this.applyWithhold(clusterId, {
                    state: "refused",
                    at: this.host.now().toISOString(),
                    imagesWithheld: null,
                    reason,
                  });
                  return;
                }
                // not yet (a race with the state) or no connection: read again
              }
            }
          } else {
            withheldAt = null;
            if (st.intakeState === "awaiting_logs")
              wait = this.deps.withholdPollMs ?? WITHHOLD_AWAITING_POLL_MS;
          }
        } catch {
          // the status could not be read: read again later
        }
        await this.sleep(wait);
      }
    } finally {
      this.loops.delete(key);
    }
  }
}
